import { makeStyles } from '@mui/styles';
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import Search from 'apollo-react/icons/Search';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { createRepositoryFromTemplate } from '../../actions/RepositoryConfigurationActions';
import { checkRepositoryName } from '../../api/documentRepositoryConfiguration';
import { REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES } from '../../constants/RepositoryConfigurationConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import ZoomDialog from '../../form-designer/controls/ssZoom/ZoomDialog';
import { getUITA } from '../../utils/commonUtils';
import {
  replaceTemplateText,
  validateRepositoryNameOnSaveAs,
} from '../../utils/repositoryConfigurationUtils';

const useStyles = makeStyles({
  modal: {
    width: 650,
    '& [class*="MuiDialogContent-root"]': {
      paddingBottom: 5,
    },
  },
  input: {
    margin: '0 0 10px 0',
  },
});

const CreateRepositoryModal = ({
  isOpen,
  initialRepositoryName = '',
  handleClose,
  setIsLoading,
  redirectToRepository,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [product, setProduct] = useState('');
  const [repositoryName, setRepositoryName] = useState(initialRepositoryName);
  const [isZoomDialogOpen, setZoomDialogOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [repositoryNameError, setRepositoryNameError] = useState({
    hasError: false,
    errorText: '',
  });

  const handleSubmit = async () => {
    !isSubmitted && setIsSubmitted(true);

    const repositoryNameError = validateRepositoryName(repositoryName);

    if (repositoryNameError.hasError || !product) {
      return;
    }

    const { repositoryExists } = await checkRepositoryName({
      repositoryName,
    });

    if (repositoryExists) {
      setRepositoryNameError({
        hasError: true,
        errorText: REPOSITORY_NAME_VALIDATION_ERROR_MESSAGES.UNIQUE,
      });
    } else {
      await dispatch(
        createRepositoryFromTemplate({
          product,
          repositoryName,
          setIsLoading,
          redirectToRepository,
        })
      );
      handleClose();
    }
  };

  const handleOnSelect = (selection = {}) => {
    const productCode = selection.PRD_CODE || '';
    const newRepositoryName = replaceTemplateText(initialRepositoryName, productCode);

    setRepositoryName(newRepositoryName);
    setZoomDialogOpen(false);
    setProduct(productCode);
    validateRepositoryName(newRepositoryName);
  };

  const handleProductBlur = () => {
    const newRepositoryName = replaceTemplateText(initialRepositoryName, product);

    setRepositoryName(newRepositoryName);
    validateRepositoryName(newRepositoryName);
  };

  const validateRepositoryName = (value) => {
    const errorObj = validateRepositoryNameOnSaveAs(value);
    setRepositoryNameError(errorObj);
    return errorObj;
  };

  return (
    <>
      <Modal
        open={isOpen}
        variant="default"
        onClose={handleClose}
        title={translateString('Create Repository')}
        buttonProps={[
          {
            label: translateString('Cancel'),
          },
          {
            label: translateString('Submit'),
            onClick: handleSubmit,
            disabled: isSubmitted && (repositoryNameError.hasError || !product),
          },
        ]}
        className={classes.modal}
        disableBackdropClick
      >
        <TextField
          fullWidth
          label={translateString('Product')}
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          onBlur={handleProductBlur}
          className={classes.input}
          required
          error={isSubmitted && !product}
          helperText={isSubmitted && !product && translateString('This field is required')}
          icon={<Search style={{ color: '#0768fd' }} />}
          iconProps={{
            onClick: () => setZoomDialogOpen(true),
          }}
        />
        <TextField
          required
          fullWidth
          label={translateString('Repository Name')}
          value={repositoryName}
          onBlur={() => validateRepositoryName(repositoryName)}
          onChange={(e) => setRepositoryName(e.target.value)}
          error={repositoryNameError.hasError}
          helperText={
            repositoryNameError.hasError ? translateString(repositoryNameError.errorText) : null
          }
          className={classes.input}
        />
      </Modal>
      <ZoomDialog
        open={isZoomDialogOpen}
        onSelect={handleOnSelect}
        onClose={() => setZoomDialogOpen(false)}
        entityName="Product"
        initialSearchName="List of Products"
        title="List of Products"
        translatedSearchName="List of Products"
        ui-ta={getUITA('ProductDialog')}
      />
    </>
  );
};

export default CreateRepositoryModal;
